import { zodResolver } from '@hookform/resolvers/zod';
import { Scanner } from '@yudiel/react-qr-scanner';
import i18next from 'i18next';
import { LandPlotIcon, Loader2, ScanQrCodeIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import { ComboBoxResponsive, OptionGroup } from '@/components/ui/combobox-responsive';
import { DatePicker } from '@/components/ui/date-picker';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { VideoUploadFormField } from '@/components/ui/video-upload-form-field';
import { FREQUENCY_UNITS } from '@/interfaces/IFrequencies';
import {
  isMissionLogistical,
  isMissionNonKamikaze,
  isMissionNonLogistical,
  isMissionNonReconnaissance,
  isMissionNonRescue,
  isMissionReconnaissance
} from '@/interfaces/IMissionType';
import { IQuestionary, LostReasonOption, UVOption, UVTypeOption } from '@/interfaces/IQuestionary';
import { isUVLost } from '@/interfaces/IUVCondition';
import { useCreateMissionReport } from '@/queries/useMissionReports';
import { formatToISO } from '@/utils/datetime';

import { EMPTY_OPTIONS, OTHER_UV_ID, VIDEO_UPLOAD_CONTAINER_ID } from './helpers';
import PayloadQuestionary, { IPayloadTypeControl } from './PayloadQuestionary';
import { validationSchema } from './validation_schema';

interface MissionReportQuestionaryProps {
  unitId: string;
  questionary: IQuestionary;
}

const MISSION_TYPE_STORAGE_KEY = 'mission_type';
const TEAM_POSITION_STORAGE_KEY = 'team_position';
const TEAM_NAME_STORAGE_KEY = 'team_name';
const COMMANDER_CALL_SIGN_STORAGE_KEY = 'commander_call_sign';
const OPERATOR_CALL_SIGN_STORAGE_KEY = 'operator_call_sign';
const UV_TYPE_STORAGE_KEY = 'uv_type';
const UV_STORAGE_KEY = 'uv';
const CONTROL_LINK_FREQUENCY_STORAGE_KEY = 'control_link_frequency';
const CONTROL_LINK_FREQUENCY_UNIT_STORAGE_KEY = 'control_link_frequency_unit';
const VIDEO_LINK_FREQUENCY_STORAGE_KEY = 'video_link_frequency';
const VIDEO_LINK_FREQUENCY_UNIT_STORAGE_KEY = 'video_link_frequency_unit';

const getUVTypesKeysInUse = (questionary: IQuestionary, selectedTeamPosition: string) => {
  const uvsInUse = questionary.uv?.filter(option => !!option.unit_position_name);

  const uvsInPosition = !!selectedTeamPosition
    ? uvsInUse?.filter(uv => uv.unit_position_name === selectedTeamPosition)
    : uvsInUse;

  return (uvsInPosition || uvsInUse)?.map(uv => uv.uv_type);
};

const getUVOptions = (
  uvOptions: UVOption[] | undefined,
  uvTypeOptions: UVTypeOption[] | undefined,
  selectedUVType: string | undefined,
  selectedTeamPosition: string | undefined
): OptionGroup[] => {
  const uvTypeLabel = uvTypeOptions?.find(type => type.value === selectedUVType)?.label;

  if (!selectedUVType && !selectedTeamPosition) {
    return [
      {
        title: 'Усі борти',
        empty: i18next.t('mission_report.uvs_empty'),
        options: uvOptions || EMPTY_OPTIONS
      }
    ];
  }

  if (!selectedUVType && !!selectedTeamPosition) {
    return [
      {
        title: `Усі борти на позиції ${selectedTeamPosition}`,
        empty: i18next.t('mission_report.uvs_empty'),
        options: uvOptions?.filter(option => option.unit_position_name === selectedTeamPosition) || EMPTY_OPTIONS
      },
      {
        title: 'Інші борти',
        empty: i18next.t('mission_report.uvs_empty'),
        options:
          uvOptions?.filter(option => option.unit_position_name !== selectedTeamPosition || !option.uv_type) ||
          EMPTY_OPTIONS
      }
    ];
  }

  if (!!selectedUVType && !selectedTeamPosition) {
    return [
      {
        title: `Всі ${uvTypeLabel}`,
        empty: i18next.t('mission_report.uvs_empty'),
        options: uvOptions?.filter(option => option.uv_type === selectedUVType || !option.uv_type) || EMPTY_OPTIONS
      }
    ];
  }

  return [
    {
      title: `${uvTypeLabel} на позиції ${selectedTeamPosition}`,
      empty: i18next.t('mission_report.uvs_empty'),
      options:
        uvOptions?.filter(
          option => option.uv_type === selectedUVType && option.unit_position_name === selectedTeamPosition
        ) || EMPTY_OPTIONS
    },
    {
      title: `Інші ${uvTypeLabel}`,
      empty: i18next.t('mission_report.uvs_empty'),
      options:
        uvOptions?.filter(
          option =>
            (option.uv_type === selectedUVType && option.unit_position_name !== selectedTeamPosition) || !option.uv_type
        ) || EMPTY_OPTIONS
    }
  ];
};

const getLostReasonGroups = (lostReasonOptions: LostReasonOption[] | undefined): OptionGroup[] => {
  const groups: OptionGroup[] = [];

  (lostReasonOptions || EMPTY_OPTIONS).forEach(option => {
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.title === option.group_label) {
      lastGroup.options.push(option);
    } else {
      groups.push({ title: option.group_label, options: [option] });
    }
  });

  return groups;
};

const MissionReportQuestionary = ({ unitId, questionary }: MissionReportQuestionaryProps) => {
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [attachmentsUploading, setAttachmentsUploading] = useState(false);

  const formSchema = validationSchema(questionary);
  type FormValues = z.infer<typeof formSchema>;

  const missionTypes = questionary.mission_type;
  const isOneMissionType = missionTypes?.length === 1;

  const cachedTeamPosition = localStorage.getItem(TEAM_POSITION_STORAGE_KEY);
  const defaultTeamPosition =
    !!cachedTeamPosition && questionary.team_position?.map(item => item.value)?.includes(cachedTeamPosition || '')
      ? cachedTeamPosition
      : '';

  const cachedTeamName = localStorage.getItem(TEAM_NAME_STORAGE_KEY);
  const defaultTeamName =
    !!cachedTeamName && questionary.team_name?.map(item => item.value)?.includes(cachedTeamName || '')
      ? cachedTeamName
      : '';

  const cachedCommanderCallSign = localStorage.getItem(COMMANDER_CALL_SIGN_STORAGE_KEY);
  const defaultCommanderCallSign =
    !!cachedCommanderCallSign &&
    questionary.commander_call_sign?.map(item => item.value)?.includes(cachedCommanderCallSign || '')
      ? cachedCommanderCallSign
      : '';

  const cachedOperatorCallSign = localStorage.getItem(OPERATOR_CALL_SIGN_STORAGE_KEY);
  const defaultOperatorCallSign =
    !!cachedOperatorCallSign &&
    questionary.operator_call_sign?.map(item => item.value)?.includes(cachedOperatorCallSign || '')
      ? cachedOperatorCallSign
      : '';

  const cachedMissionType = localStorage.getItem(MISSION_TYPE_STORAGE_KEY);
  const invalidatedMissionType = questionary.mission_type?.some(item => item.value === cachedMissionType)
    ? cachedMissionType
    : '';
  const defaultMissionType = isOneMissionType ? missionTypes[0].value : invalidatedMissionType || '';

  const cachedUVType = localStorage.getItem(UV_TYPE_STORAGE_KEY);
  const defaultUVType =
    !!cachedUVType && questionary.uv_type?.map(item => item.value)?.includes(cachedUVType || '') ? cachedUVType : '';

  const cachedUV = localStorage.getItem(UV_STORAGE_KEY);
  const defaultUV = !!cachedUV && questionary.uv?.map(item => item.value)?.includes(cachedUV || '') ? cachedUV : '';

  const cachedControlLinkFrequency = localStorage.getItem(CONTROL_LINK_FREQUENCY_STORAGE_KEY);
  const defaultControlLinkFrequency =
    questionary.control_link_frequency && !!cachedControlLinkFrequency
      ? parseFloat(cachedControlLinkFrequency)
      : undefined;
  const defaultControlLinkFrequencyUnit = questionary.control_link_frequency
    ? localStorage.getItem(CONTROL_LINK_FREQUENCY_UNIT_STORAGE_KEY) || FREQUENCY_UNITS.MHZ
    : undefined;

  const cachedVideoLinkFrequency = localStorage.getItem(VIDEO_LINK_FREQUENCY_STORAGE_KEY);
  const defaultVideoLinkFrequency =
    questionary.video_link_frequency && !!cachedVideoLinkFrequency ? parseFloat(cachedVideoLinkFrequency) : undefined;
  const defaultVideoLinkFrequencyUnit = questionary.video_link_frequency
    ? localStorage.getItem(VIDEO_LINK_FREQUENCY_UNIT_STORAGE_KEY) || FREQUENCY_UNITS.MHZ
    : undefined;

  const now = new Date();
  const defaultStartDate = now.toLocaleDateString('uk-UA', { timeZone: 'Europe/Kyiv' });
  const defaultStartTime = now.toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Kyiv'
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      team_position: defaultTeamPosition,
      team_name: defaultTeamName,
      commander_call_sign: defaultCommanderCallSign,
      operator_call_sign: defaultOperatorCallSign,
      mission_type: defaultMissionType,
      mission_start_date: defaultStartDate,
      mission_start_time: defaultStartTime,
      mission_end_date: questionary.mission_ended_at ? defaultStartDate : '',
      mission_end_time: '',
      mission_number: '',
      uv_type: defaultUVType,
      uv: defaultUV,
      other_uv: '',
      battery_id: '',
      control_link_frequency: defaultControlLinkFrequency,
      control_link_frequency_unit: defaultControlLinkFrequencyUnit,
      video_link_frequency: defaultVideoLinkFrequency,
      video_link_frequency_unit: defaultVideoLinkFrequencyUnit,
      uv_condition: '',
      uv_lost_reason: '',
      uv_last_known_coordinates: '',
      target_code: '',
      target_coordinates: '',
      target_type_id: '',
      payload_weight: 0,
      travel_distance: 0,
      payloads: [{ payload_type: '', payload_count: 1 }],
      mission_status: '',
      mission_failure_reason: '',
      attachments: [{ mediaId: '', fileName: '' }],
      stream: '',
      team_notes: ''
    }
  });

  const { mutateAsync: createMissionReport } = useCreateMissionReport();

  const onSubmit = async (data: FormValues) => {
    if (attachmentsUploading) {
      const element = document.getElementById(VIDEO_UPLOAD_CONTAINER_ID);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      toast.error(i18next.t('validations.video_upload_in_progress'));
    } else {
      localStorage.setItem(TEAM_POSITION_STORAGE_KEY, data.team_position);
      if (data.team_name) localStorage.setItem(TEAM_NAME_STORAGE_KEY, data.team_name);
      if (data.commander_call_sign) localStorage.setItem(COMMANDER_CALL_SIGN_STORAGE_KEY, data.commander_call_sign);
      localStorage.setItem(OPERATOR_CALL_SIGN_STORAGE_KEY, data.operator_call_sign);
      localStorage.setItem(UV_TYPE_STORAGE_KEY, data.uv_type);
      localStorage.setItem(MISSION_TYPE_STORAGE_KEY, data.mission_type);

      if (data.uv && data.uv !== OTHER_UV_ID) {
        localStorage.setItem(UV_STORAGE_KEY, data.uv);
      }
      if (!!data.control_link_frequency) {
        localStorage.setItem(CONTROL_LINK_FREQUENCY_STORAGE_KEY, data.control_link_frequency.toString());
      }
      if (!!data.control_link_frequency_unit) {
        localStorage.setItem(CONTROL_LINK_FREQUENCY_UNIT_STORAGE_KEY, data.control_link_frequency_unit);
      }
      if (!!data.video_link_frequency) {
        localStorage.setItem(VIDEO_LINK_FREQUENCY_STORAGE_KEY, data.video_link_frequency.toString());
      }
      if (!!data.video_link_frequency_unit) {
        localStorage.setItem(VIDEO_LINK_FREQUENCY_UNIT_STORAGE_KEY, data.video_link_frequency_unit);
      }

      // Transform attachments array to array of IDs for the backend
      // Transform date + time into ISO 8601 datetime strings
      const { mission_start_date, mission_start_time, mission_end_date, mission_end_time, ...restData } = data;

      const transformedData = {
        ...restData,
        attachments: data.attachments?.filter(att => att.mediaId).map(att => att.mediaId) || [],
        mission_started_at: formatToISO(mission_start_date, mission_start_time),
        mission_ended_at:
          mission_end_date && mission_end_time ? formatToISO(mission_end_date, mission_end_time) : undefined
      };

      await createMissionReport({ unitId, data: transformedData })
        .then(() => navigate('/good-job'))
        .catch(error => {
          if (error?.response?.data?.status === 'deprecated_payload') {
            toast.error(i18next.t('errors.deprecated_payload.message'), {
              action: {
                label: i18next.t('errors.deprecated_payload.action'),
                onClick: () => window.location.reload()
              }
            });
          } else {
            toast.error(i18next.t('errors.generic_mutation.message'));
          }
        });
    }
  };

  const selectedTeamPosition = form.watch('team_position');

  const selectedMissionType = form.watch('mission_type');
  const missionLogistical = isMissionLogistical(selectedMissionType);
  const missionNonLogistical = isMissionNonLogistical(selectedMissionType);
  const missionNonRescue = isMissionNonRescue(selectedMissionType);
  const missionNonKamikaze = isMissionNonKamikaze(selectedMissionType);
  const missionReconnaissance = isMissionReconnaissance(selectedMissionType);
  const missionNonReconnaissance = isMissionNonReconnaissance(selectedMissionType);
  const uvLost = isUVLost(form.watch('uv_condition'));

  const uvTypesKeysInUse = getUVTypesKeysInUse(questionary, selectedTeamPosition);
  const uvTypesInUse = questionary.uv_type?.filter(uvType => uvTypesKeysInUse?.includes(uvType.value));
  const otherUVTypes = questionary.uv_type?.filter(uvType => !uvTypesKeysInUse?.includes(uvType.value));

  const selectedUVType = form.watch('uv_type');
  const selectedUVCategory = questionary.uv_type?.find(item => item.value === selectedUVType)?.category;

  const targetTypes = (questionary.target_type_v2 || EMPTY_OPTIONS).filter(
    option => !selectedMissionType || !option.mission_types || option.mission_types?.includes(selectedMissionType)
  );
  const targetTypesOptions = Object.values(
    targetTypes.reduce((acc, item) => {
      const key = item.category;

      acc[key] ??= { title: key, options: [] };
      acc[key].options.push(item);

      return acc;
    }, {})
  ) as OptionGroup[];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-8">
        {!isOneMissionType && (
          <FormField
            control={form.control}
            name="mission_type"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{i18next.t('mission_report.mission_type')}</FormLabel>
                <ComboBoxResponsive
                  drawerTitle={i18next.t('mission_report.mission_type')}
                  groups={[{ options: questionary.mission_type || EMPTY_OPTIONS }]}
                  value={field.value}
                  onChange={field.onChange}
                  form
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {questionary.attachments && missionNonLogistical && missionNonRescue && missionNonReconnaissance && (
          <VideoUploadFormField
            containerId={VIDEO_UPLOAD_CONTAINER_ID}
            name="attachments"
            control={form.control}
            onUploadStateChange={setAttachmentsUploading}
            allowedFileCount={6}
          />
        )}

        {!!selectedMissionType && (
          <>
            <div className="flex flex-col gap-5">
              <h1 className="text-2xl">{i18next.t('mission_report.section_team')}</h1>

              <FormField
                control={form.control}
                name="team_position"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{i18next.t('mission_report.team_position')}</FormLabel>
                    <ComboBoxResponsive
                      drawerTitle={i18next.t('mission_report.team_position')}
                      groups={[{ options: questionary.team_position || EMPTY_OPTIONS }]}
                      value={field.value}
                      onChange={field.onChange}
                      form
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {questionary.team_name && (
                <FormField
                  control={form.control}
                  name="team_name"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.team_name')}</FormLabel>
                      <ComboBoxResponsive
                        drawerTitle={i18next.t('mission_report.team_name')}
                        groups={[{ options: questionary.team_name || EMPTY_OPTIONS }]}
                        value={field.value}
                        onChange={field.onChange}
                        form
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {questionary.commander_call_sign && (
                <FormField
                  control={form.control}
                  name="commander_call_sign"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.commander_call_sign')}</FormLabel>
                      <ComboBoxResponsive
                        drawerTitle={i18next.t('mission_report.commander_call_sign')}
                        groups={[{ options: questionary.commander_call_sign || EMPTY_OPTIONS }]}
                        value={field.value}
                        onChange={field.onChange}
                        form
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="operator_call_sign"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{i18next.t('mission_report.operator_call_sign')}</FormLabel>
                    <ComboBoxResponsive
                      drawerTitle={i18next.t('mission_report.operator_call_sign')}
                      groups={[{ options: questionary.operator_call_sign || EMPTY_OPTIONS }]}
                      value={field.value}
                      onChange={field.onChange}
                      form
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-5">
              <h1 className="text-2xl">{i18next.t('mission_report.section_mission')}</h1>

              <div className="flex gap-5">
                <FormField
                  control={form.control}
                  name="mission_start_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col w-full">
                      <FormLabel>{i18next.t('mission_report.mission_start_date')}</FormLabel>
                      <DatePicker value={field.value} onChange={field.onChange} form />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mission_start_time"
                  render={({ field }) => (
                    <FormItem className="flex flex-col w-full">
                      <FormLabel>{i18next.t('mission_report.mission_start_time')}</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {questionary.mission_ended_at && (
                <div className="flex gap-5">
                  <FormField
                    control={form.control}
                    name="mission_end_date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col w-full">
                        <FormLabel>{i18next.t('mission_report.mission_end_date')}</FormLabel>
                        <DatePicker value={field.value || ''} onChange={field.onChange} form />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mission_end_time"
                    render={({ field }) => (
                      <FormItem className="flex flex-col w-full">
                        <FormLabel>{i18next.t('mission_report.mission_end_time')}</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {questionary.mission_number && (
                <FormField
                  control={form.control}
                  name="mission_number"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.mission_number')}</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder={i18next.t('placeholders.number')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {questionary.target_code && missionNonLogistical && missionNonRescue && missionNonReconnaissance && (
                <FormField
                  control={form.control}
                  name="target_code"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.target_code')}</FormLabel>
                      <ComboBoxResponsive
                        drawerTitle={i18next.t('mission_report.target_code')}
                        groups={[{ options: questionary.target_code || EMPTY_OPTIONS }]}
                        value={field.value}
                        onChange={field.onChange}
                        form
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {questionary.target_coordinates &&
                missionNonLogistical &&
                missionNonRescue &&
                missionNonReconnaissance && (
                  <FormField
                    control={form.control}
                    name="target_coordinates"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{i18next.t('mission_report.target_coordinates')}</FormLabel>
                        <FormControl>
                          <Input
                            ref={field.ref}
                            name={field.name}
                            type="text"
                            placeholder={i18next.t('placeholders.coordinates')}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={e => {
                              const filtered = e.target.value.replace(/[^A-Za-z0-9 ]+/g, '');
                              field.onChange(filtered);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

              {missionNonLogistical && missionNonRescue && missionNonReconnaissance && (
                <FormField
                  control={form.control}
                  name="target_type_id"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.target_type')}</FormLabel>
                      <ComboBoxResponsive
                        drawerTitle={i18next.t('mission_report.target_type')}
                        groups={targetTypesOptions}
                        value={field.value}
                        onChange={field.onChange}
                        form
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {questionary.payload_weight && missionLogistical && (
                <FormField
                  control={form.control}
                  name="payload_weight"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.payload_weight')}</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder={i18next.t('placeholders.number')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {questionary.travel_distance && (
                <FormField
                  control={form.control}
                  name="travel_distance"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.travel_distance')}</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder={i18next.t('placeholders.number')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="flex flex-col gap-5">
              <h1 className="text-2xl">{i18next.t('mission_report.section_uv')}</h1>

              <FormField
                control={form.control}
                name="uv_type"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{i18next.t('mission_report.uv_type')}</FormLabel>
                    <ComboBoxResponsive
                      drawerTitle={i18next.t('mission_report.uv_type')}
                      groups={[
                        {
                          title: i18next.t('mission_report.uvs_in_use'),
                          empty: i18next.t('mission_report.uvs_empty'),
                          options: uvTypesInUse || EMPTY_OPTIONS
                        },
                        {
                          title: i18next.t('mission_report.uvs_other'),
                          empty: i18next.t('mission_report.uvs_empty'),
                          options: otherUVTypes || EMPTY_OPTIONS
                        }
                      ]}
                      value={field.value}
                      onChange={field.onChange}
                      form
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                {questionary.uv && (
                  <FormField
                    control={form.control}
                    name="uv"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{i18next.t('mission_report.uv')}</FormLabel>
                        <div className="flex gap-2 items-center">
                          <ComboBoxResponsive
                            drawerTitle={i18next.t('mission_report.uv')}
                            groups={getUVOptions(
                              questionary.uv,
                              questionary.uv_type,
                              selectedUVType,
                              selectedTeamPosition
                            )}
                            value={field.value}
                            onChange={field.onChange}
                            form
                            className="flex-1"
                          />
                          <Button type="button" size="lg" variant="outline" onClick={() => setScannerOpen(true)}>
                            <ScanQrCodeIcon />
                          </Button>
                        </div>
                        <FormMessage />

                        {scannerOpen && (
                          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="p-4 rounded shadow-lg max-w-md w-full">
                              <h2 className="text-lg text-center font-semibold mb-4">
                                {i18next.t('qr_code_scanner.scan_qr_code')}
                              </h2>
                              <Scanner
                                constraints={{ facingMode: 'environment' }}
                                onScan={result => {
                                  if (result) {
                                    const rawValue = result[0]?.rawValue;
                                    if (!questionary.uv?.some(option => option.value === rawValue)) {
                                      toast.error(i18next.t('qr_code_scanner.qr_code_invalid'));
                                    } else {
                                      form.setValue('uv', result[0]?.rawValue);
                                    }
                                  }

                                  setScannerOpen(false);
                                }}
                              />
                              <Button
                                type="button"
                                variant="secondary"
                                className="mt-4 w-full"
                                onClick={() => setScannerOpen(false)}
                              >
                                {i18next.t('controls.close')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </FormItem>
                    )}
                  />
                )}

                {form.watch('uv') === OTHER_UV_ID && (
                  <FormField
                    control={form.control}
                    name="other_uv"
                    render={({ field }) => (
                      <FormItem className="flex flex-col mt-2">
                        <FormControl>
                          <Input type="text" placeholder={i18next.t('placeholders.text')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {questionary.control_link_frequency && (
                <FormField
                  control={form.control}
                  name="control_link_frequency"
                  render={({ field }) => {
                    const selectedUnit = form.watch('control_link_frequency_unit');
                    const description =
                      selectedUnit === FREQUENCY_UNITS.GHZ
                        ? i18next.t('placeholders.frequency_ghz_range')
                        : i18next.t('placeholders.frequency_mhz_range');

                    return (
                      <FormItem className="flex flex-col">
                        <FormLabel>{i18next.t('mission_report.control_link_frequency')}</FormLabel>
                        <div className="flex gap-2 items-center">
                          <FormControl>
                            <Input type="number" min={0} step="0.01" {...field} />
                          </FormControl>
                          <FormField
                            control={form.control}
                            name="control_link_frequency_unit"
                            render={({ field: unitField }) => (
                              <div className="flex items-center">
                                <ToggleGroup
                                  variant="outline"
                                  type="single"
                                  value={unitField.value}
                                  onValueChange={value => {
                                    if (value) {
                                      unitField.onChange(value);
                                    }
                                  }}
                                >
                                  <ToggleGroupItem value={FREQUENCY_UNITS.MHZ} aria-label="MHz" className="w-16">
                                    {i18next.t('mission_report.frequency_mhz')}
                                  </ToggleGroupItem>
                                  <ToggleGroupItem value={FREQUENCY_UNITS.GHZ} aria-label="GHz" className="w-16">
                                    {i18next.t('mission_report.frequency_ghz')}
                                  </ToggleGroupItem>
                                </ToggleGroup>
                              </div>
                            )}
                          />
                        </div>
                        {!form.formState.errors.control_link_frequency && (
                          <FormDescription>{description}</FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}

              {questionary.video_link_frequency && (
                <FormField
                  control={form.control}
                  name="video_link_frequency"
                  render={({ field }) => {
                    const selectedUnit = form.watch('video_link_frequency_unit');
                    const description =
                      selectedUnit === FREQUENCY_UNITS.GHZ
                        ? i18next.t('placeholders.frequency_ghz_range')
                        : i18next.t('placeholders.frequency_mhz_range');

                    return (
                      <FormItem className="flex flex-col">
                        <FormLabel>{i18next.t('mission_report.video_link_frequency')}</FormLabel>
                        <div className="flex gap-2 items-center">
                          <FormControl>
                            <Input type="number" min={0} step="0.01" {...field} />
                          </FormControl>
                          <FormField
                            control={form.control}
                            name="video_link_frequency_unit"
                            render={({ field: unitField }) => (
                              <div className="flex items-center">
                                <ToggleGroup
                                  variant="outline"
                                  type="single"
                                  value={unitField.value}
                                  onValueChange={value => {
                                    if (value) {
                                      unitField.onChange(value);
                                    }
                                  }}
                                >
                                  <ToggleGroupItem value={FREQUENCY_UNITS.MHZ} aria-label="MHz" className="w-16">
                                    {i18next.t('mission_report.frequency_mhz')}
                                  </ToggleGroupItem>
                                  <ToggleGroupItem value={FREQUENCY_UNITS.GHZ} aria-label="GHz" className="w-16">
                                    {i18next.t('mission_report.frequency_ghz')}
                                  </ToggleGroupItem>
                                </ToggleGroup>
                              </div>
                            )}
                          />
                        </div>
                        {!form.formState.errors.video_link_frequency && (
                          <FormDescription>{description}</FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}

              {missionNonKamikaze && (
                <FormField
                  control={form.control}
                  name="battery_id"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.battery_id')}</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder={i18next.t('placeholders.text')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {missionNonKamikaze && (
                <FormField
                  control={form.control}
                  name="uv_condition"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.uv_condition')}</FormLabel>
                      <ComboBoxResponsive
                        drawerTitle={i18next.t('mission_report.uv_condition')}
                        groups={[{ options: questionary.uv_condition || EMPTY_OPTIONS }]}
                        value={field.value}
                        onChange={field.onChange}
                        form
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {questionary.uv_lost_reason && uvLost && (
                <FormField
                  control={form.control}
                  name="uv_lost_reason"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.uv_lost_reason')}</FormLabel>
                      <ComboBoxResponsive
                        drawerTitle={i18next.t('mission_report.uv_lost_reason')}
                        groups={getLostReasonGroups(questionary.uv_lost_reason)}
                        value={field.value}
                        onChange={field.onChange}
                        form
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {uvLost && (
                <FormField
                  control={form.control}
                  name="uv_last_known_coordinates"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.uv_last_known_coordinates')}</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder={i18next.t('placeholders.coordinates')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {missionNonLogistical && missionNonRescue && missionNonReconnaissance && (
              <PayloadQuestionary
                questionary={questionary}
                control={form.control as unknown as IPayloadTypeControl}
                enteredPayloads={form.watch('payloads')}
                selectedUVCategory={selectedUVCategory}
                allowedPayloadCount={4}
              />
            )}

            <div className="flex flex-col gap-5">
              <h1 className="text-2xl">{i18next.t('mission_report.section_result')}</h1>

              {missionNonReconnaissance && (
                <FormField
                  control={form.control}
                  name="mission_status"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.mission_status')}</FormLabel>
                      <ComboBoxResponsive
                        drawerTitle={i18next.t('mission_report.mission_status')}
                        groups={[
                          {
                            options: (questionary.mission_status_v2 || EMPTY_OPTIONS).filter(
                              option =>
                                !selectedMissionType ||
                                !option.mission_types ||
                                option.mission_types?.includes(selectedMissionType)
                            )
                          }
                        ]}
                        value={field.value}
                        onChange={field.onChange}
                        form
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="mission_failure_reason"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {missionLogistical || missionReconnaissance
                        ? i18next.t('mission_report.mission_failure_reason_non_combat')
                        : i18next.t('mission_report.mission_failure_reason')}
                    </FormLabel>
                    <ComboBoxResponsive
                      drawerTitle={
                        missionLogistical || missionReconnaissance
                          ? i18next.t('mission_report.mission_failure_reason_non_combat')
                          : i18next.t('mission_report.mission_failure_reason')
                      }
                      groups={[
                        {
                          options: (questionary.mission_failure_reason_v2 || EMPTY_OPTIONS).filter(
                            option =>
                              (!selectedMissionType ||
                                !option.mission_types ||
                                option.mission_types?.includes(selectedMissionType)) &&
                              (!selectedUVCategory ||
                                !option.uv_categories ||
                                option.uv_categories?.includes(selectedUVCategory))
                          )
                        }
                      ]}
                      value={field.value}
                      onChange={field.onChange}
                      form
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {questionary.stream && (
                <FormField
                  control={form.control}
                  name="stream"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{i18next.t('mission_report.stream')}</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder={i18next.t('placeholders.text')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="team_notes"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{i18next.t('mission_report.team_notes')}</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={form.formState.isSubmitting}
              className="w-full py-2 mb-12 bg-emerald-500 hover:bg-emerald-600"
            >
              <LandPlotIcon /> {i18next.t('mission_report.create_mission_report')}
              {form.formState.isSubmitting && <Loader2 className="animate-spin" />}
            </Button>
          </>
        )}
      </form>
    </Form>
  );
};

export default MissionReportQuestionary;
